// @ts-nocheck
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Typography from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import Block from "@store/Library/Components/Toolkit/Block";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import {
    ActuatorModel,
    ConfigureResponseModel,
    ElastomerModel,
    GateMaterialModel,
    SelectorsResponseModel,
} from "@store/Framework/Types/ValveConfiguratorApiModels";
import React, { FC, useMemo } from "react";
import { connect } from "react-redux";
import styled, { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const v = state.store.data.valveConfigurator;
    return {
        selectors: v.selectors,
        selectedSeries: v.selectedSeries,
        selectedSize: v.selectedSize,
        selectedActuator: v.selectedActuator,
        selectedGateMaterial: v.selectedGateMaterial,
        selectedElastomer: v.selectedElastomer,
        flangeCode: v.flangeCode,
        availableActuators: v.availableActuators,
        availableGateMaterials: v.availableGateMaterials,
        availableElastomers: v.availableElastomers,
        configurationResult: v.configurationResult,
    };
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps>;

const Panel = styled.div`
    border: 1px solid ${getColor("common.border")};
    border-radius: ${pxToRem(10)};
    padding: ${pxToRem(24)};
    background: ${getColor("common.background")};
`;

const ImageBox = styled.div`
    min-height: ${pxToRem(280)};
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;

    img {
        max-height: ${pxToRem(360)};
        max-width: 100%;
        object-fit: contain;
        transition: opacity 250ms ease;
    }
`;

const ModelArea = styled.div`
    border-top: 1px solid ${getColor("common.border")};
    margin-top: ${pxToRem(16)};
    padding-top: ${pxToRem(16)};
    text-align: center;
`;

const ModelText = styled.div<{ $dim: boolean }>`
    font-size: ${pxToRem(22)};
    font-weight: 600;
    margin-top: ${pxToRem(4)};
    color: ${({ $dim }) => ($dim ? getColor("placeholder") : getColor("text.main"))};
    font-variant-numeric: tabular-nums;
`;

function buildPreviewModel(
    series: string | null,
    size: number | null,
    flangeCode: string,
    gate: GateMaterialModel | undefined,
    el: ElastomerModel | undefined,
    act: ActuatorModel | undefined,
): string {
    if (!series) {
        return "____-______";
    }

    const sz = size === null ? "__" : String(size);
    if (size === null) {
        return `${series}${sz}-______`;
    }

    const f = flangeCode?.trim() || "_";
    const g = gate?.code ?? "_";
    const e = el?.code ?? "_";
    const a = act?.code ?? "_";
    return `${series}${size}-${f}${g}${e}${a}`;
}

function previewImageFor(selectors: SelectorsResponseModel | null, series: string | null): string {
    if (!selectors || !series) {
        return "";
    }

    const vt = selectors.valveTypes?.find(v => v.code === series);
    return vt?.previewImageUrl?.trim() ?? "";
}

const ValveConfiguratorImageWidget: FC<Props> = ({
    selectors,
    selectedSeries,
    selectedSize,
    selectedActuator,
    selectedGateMaterial,
    selectedElastomer,
    flangeCode,
    availableActuators,
    availableGateMaterials,
    availableElastomers,
    configurationResult,
}) => {
    const previewSrc = useMemo(() => previewImageFor(selectors, selectedSeries), [selectors, selectedSeries]);

    const gateObj = availableGateMaterials.find(g => g.label === selectedGateMaterial);
    const elObj = availableElastomers.find(e => e.label === selectedElastomer);
    const actObj = availableActuators.find(a => a.label === selectedActuator);

    const previewModel = useMemo(
        () => buildPreviewModel(selectedSeries, selectedSize, flangeCode, gateObj, elObj, actObj),
        [selectedSeries, selectedSize, flangeCode, gateObj, elObj, actObj],
    );

    const submittedModel: string | null =
        configurationResult && (configurationResult as ConfigureResponseModel).isQto === false
            ? (configurationResult as { modelNumber: string }).modelNumber
            : null;

    const displayModel = submittedModel ?? previewModel;
    const isDim = !submittedModel;

    return (
        <Block>
            <Panel>
                <ImageBox>
                    {previewSrc ? (
                        <img src={previewSrc} alt={selectedSeries ?? ""} />
                    ) : (
                        <Typography
                            variant="p"
                            css={css`
                                color: ${getColor("text.accent")};
                            `}
                        >
                            {translate("FlsValveConfigurator.Image.NoSeries")}
                        </Typography>
                    )}
                </ImageBox>
                <ModelArea>
                    <Typography
                        variant="p"
                        css={css`
                            font-size: ${pxToRem(11)};
                            color: ${getColor("text.accent")};
                            text-transform: uppercase;
                            letter-spacing: 0.08em;
                            margin: 0;
                        `}
                    >
                        {translate("FlsValveConfigurator.Image.ModelPreview")}
                    </Typography>
                    <ModelText $dim={isDim}>{displayModel}</ModelText>
                </ModelArea>
            </Panel>
        </Block>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(ValveConfiguratorImageWidget),
    definition: {
        group: "Products",
        icon: "image",
        displayName: "Valve Configurator - Image and Model Preview",
        fieldDefinitions: [],
    },
};

export default widgetModule;
